import {
  DataTable,
  EmptyState,
  ErrorState,
  FormField,
  Input,
  PageHeader,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Skeleton,
  VehicleCard,
  VehicleStatusChip,
  type ColumnDef,
} from '@zuruflex/ui';
import { formatMoneyIn, toLocale } from '@zuruflex/i18n';
import { LISTING_STATUSES, VEHICLE_CLASSES, VEHICLE_STATUSES, type Vehicle } from '@zuruflex/types';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { LayoutGrid, Rows3 } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '../lib/api.js';
import { useTableLabels } from '../lib/table-labels.js';

const ANY = 'any';

const VIEWS = [
  { value: 'grid', icon: LayoutGrid, key: 'fleet.viewGrid' },
  { value: 'table', icon: Rows3, key: 'fleet.viewTable' },
] as const;

type View = (typeof VIEWS)[number]['value'];

export function FleetPage() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation(['admin', 'common']);
  const locale = toLocale(i18n.language);
  const tableLabels = useTableLabels();
  const [q, setQ] = useState('');
  const [status, setStatus] = useState(ANY);
  const [vehicleClass, setVehicleClass] = useState(ANY);
  /*
   * In the URL, because "every car waiting on review" is the queue listing
   * review is worked from, and a queue should be a link somebody can send.
   */
  const [params, setParams] = useSearchParams();
  const listing = params.get('listing') ?? ANY;
  const setListing = (value: string) => {
    const next = new URLSearchParams(params);
    if (value === ANY) next.delete('listing');
    else next.set('listing', value);
    setParams(next, { replace: true });
  };
  /*
   * Cards sell a car and rows compare a hundred of them. Which one is
   * wanted depends on the question being asked this minute, not on the
   * page, so it is a switch rather than a decision taken for the user.
   */
  const [view, setView] = useState<View>('grid');

  const { data, isPending, error, refetch } = useQuery({
    queryKey: ['fleet', { q, status, vehicleClass, listing }],
    queryFn: () =>
      api.listVehicles({
        perPage: 24,
        ...(q ? { q } : {}),
        ...(status !== ANY ? { status: status as never } : {}),
        ...(vehicleClass !== ANY ? { vehicleClass: [vehicleClass] as never } : {}),
        ...(listing !== ANY ? { listingStatus: listing as never } : {}),
      }),
    // Keeps the current grid on screen while a filter change is in flight,
    // instead of flashing skeletons on every keystroke.
    placeholderData: keepPreviousData,
  });

  // Columns depend on the language, so they are rebuilt when it changes.
  const columns = useMemo<ColumnDef<Vehicle>[]>(
    () => [
      {
        accessorKey: 'make',
        header: t('common:field.vehicle'),
        cell: ({ row }) => (
          <div className="min-w-0">
            <p className="truncate font-semibold">
              {row.original.make} {row.original.model}
            </p>
            <p className="text-xs text-text-muted">{row.original.year}</p>
          </div>
        ),
      },
      {
        accessorKey: 'plateNumber',
        header: t('common:field.plate'),
        cell: ({ row }) => <span className="font-mono text-sm">{row.original.plateNumber}</span>,
      },
      {
        accessorKey: 'vehicleClass',
        header: t('common:field.class'),
        cell: ({ row }) => t(`common:vehicleClass.${row.original.vehicleClass}`),
      },
      {
        accessorKey: 'status',
        header: t('common:field.status'),
        cell: ({ row }) => (
          <VehicleStatusChip
            status={row.original.status}
            label={t(`common:vehicleStatus.${row.original.status}`)}
          />
        ),
      },
      {
        accessorKey: 'listingStatus',
        header: t('fleet.listing'),
        cell: ({ row }) => (
          <span className="text-sm text-text-muted">
            {t(`common:listingStatus.${row.original.listingStatus}`)}
          </span>
        ),
      },
      {
        accessorKey: 'dailyRate',
        header: t('common:field.dailyRate'),
        cell: ({ row }) => (
          <span className="font-semibold tabular-nums">
            {formatMoneyIn(row.original.dailyRate, row.original.currency, locale)}
          </span>
        ),
      },
    ],
    [t, locale],
  );

  return (
    <>
      {/*
        No "add vehicle" action. Every car belongs to a partner, and the API
        refuses a staff-created listing outright — the house fleet goes up
        through Zuruflex's own partner login like anyone else's. A button here
        could only ever have promised something the server says no to.
      */}
      <PageHeader
        title={t('fleet.title')}
        description={data ? t('fleet.count', { count: data.total }) : t('fleet.loading')}
        action={
          <div
            role="group"
            aria-label={t('fleet.view')}
            className="inline-flex rounded-pill bg-surface-sunken p-1"
          >
            {VIEWS.map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => setView(option.value)}
                aria-pressed={view === option.value}
                className={[
                  'inline-flex items-center gap-2 rounded-pill px-3.5 py-1.5 text-xs font-semibold transition-colors',
                  view === option.value
                    ? 'bg-surface-active text-text shadow-soft'
                    : 'text-text-muted hover:text-text',
                ].join(' ')}
              >
                <option.icon className="size-4" />
                {t(option.key)}
              </button>
            ))}
          </div>
        }
      />

      <div className="mb-8 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <FormField label={t('common:action.search')}>
          {(field) => (
            <Input
              {...field}
              placeholder={t('fleet.searchPlaceholder')}
              value={q}
              onChange={(event) => setQ(event.target.value)}
            />
          )}
        </FormField>

        <FormField label={t('common:field.status')}>
          {(field) => (
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger id={field.id}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ANY}>{t('fleet.anyStatus')}</SelectItem>
                {VEHICLE_STATUSES.map((value) => (
                  <SelectItem key={value} value={value}>
                    {t(`common:vehicleStatus.${value}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </FormField>

        <FormField label={t('fleet.listing')}>
          {(field) => (
            <Select value={listing} onValueChange={setListing}>
              <SelectTrigger id={field.id}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ANY}>{t('fleet.anyListing')}</SelectItem>
                {LISTING_STATUSES.map((value) => (
                  <SelectItem key={value} value={value}>
                    {t(`common:listingStatus.${value}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </FormField>

        <FormField label={t('common:field.class')}>
          {(field) => (
            <Select value={vehicleClass} onValueChange={setVehicleClass}>
              <SelectTrigger id={field.id}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ANY}>{t('fleet.anyClass')}</SelectItem>
                {VEHICLE_CLASSES.map((value) => (
                  <SelectItem key={value} value={value}>
                    {t(`common:vehicleClass.${value}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </FormField>
      </div>

      {error ? (
        <ErrorState error={error} onRetry={() => void refetch()} />
      ) : view === 'table' ? (
        /*
         * The list is already paged and filtered by the server, so the
         * table only sorts and shows what it was handed.
         */
        <DataTable
          labels={tableLabels}
          columns={columns}
          data={data?.items ?? []}
          isLoading={isPending}
          paginate={false}
          onRowClick={(row) => navigate(`/fleet/${row.slug}`)}
          emptyMessage={t('fleet.noMatch')}
        />
      ) : isPending ? (
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5">
          {Array.from({ length: 10 }, (_, index) => (
            <Skeleton key={index} className="h-[26rem] w-full" />
          ))}
        </div>
      ) : data.items.length === 0 ? (
        <EmptyState title={t('fleet.noMatch')} description={t('fleet.noMatchBody')} />
      ) : (
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5">
          {data.items.map((vehicle) => (
            <VehicleCard
              key={vehicle.id}
              vehicle={vehicle}
              showStatus
              actionLabel={t('common:action.open')}
              onSelect={() => navigate(`/fleet/${vehicle.slug}`)}
            />
          ))}
        </div>
      )}
    </>
  );
}
